/**
 * JSON-LD and SSR helpers for public blog pages.
 *
 * @see https://developers.google.com/search/docs/appearance/publication-dates
 * @see https://developers.google.com/search/docs/appearance/structured-data/speakable
 * @see https://developers.google.com/search/docs/appearance/structured-data/breadcrumb
 * @see https://developers.google.com/search/docs/appearance/enable-web-stories
 */
const SITE_URL = 'https://gab.ai';
const LOGO_URL = 'https://cdn.gab.ai/static/media/gab-ai-logo.png';
const ORG_NAME = 'Gab AI Inc.';

/** CSS selectors for SpeakableSpecification — must match DOM on /blog/:slug pages. */
export const BLOG_SPEAKABLE_SELECTORS = [
  '#blog-speakable-headline',
  '#blog-speakable-summary',
];

function absUrl(canonicalPath) {
  if (!canonicalPath || typeof canonicalPath !== 'string') return SITE_URL;
  if (canonicalPath.startsWith('http://') || canonicalPath.startsWith('https://')) {
    return canonicalPath.replace(/\/$/, '');
  }
  const path = canonicalPath.startsWith('/') ? canonicalPath : `/${canonicalPath}`;
  return `${SITE_URL}${path}`;
}

export function toIsoDate(value) {
  if (!value) return undefined;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toISOString();
}

function listItem(position, name, item) {
  const row = { '@type': 'ListItem', position, name };
  if (item) row.item = item;
  return row;
}

export function resolveBlogCategory(post) {
  const ref = post?.blogPostCategory;
  const categoryName = (post?.category && String(post.category).trim())
    || (ref && typeof ref === 'object' && ref.name ? String(ref.name).trim() : '')
    || '';
  const categorySlug = (ref && typeof ref === 'object' && ref.slug ? String(ref.slug).trim() : '')
    || (post?.categorySlug ? String(post.categorySlug).trim() : '');
  return { categoryName, categorySlug };
}

export function buildBlogPostBreadcrumbList({
  pageUrl,
  title,
  categoryName,
  categorySlug,
}) {
  const items = [
    listItem(1, 'Home', SITE_URL),
    listItem(2, 'Blog', `${SITE_URL}/blog`),
  ];
  if (categorySlug && categoryName) {
    items.push(
      listItem(3, categoryName, `${SITE_URL}/blog/category/${encodeURIComponent(categorySlug)}`),
    );
    items.push(listItem(4, title));
  } else {
    items.push(listItem(3, title));
  }

  return {
    '@type': 'BreadcrumbList',
    '@id': `${pageUrl}#breadcrumb`,
    itemListElement: items,
  };
}

function parseCustomBlogPosting(schemaJson) {
  if (!schemaJson) return null;
  try {
    return typeof schemaJson === 'string' ? JSON.parse(schemaJson) : schemaJson;
  } catch {
    return null;
  }
}

/**
 * Rich JSON-LD graph for a single blog post.
 */
export function buildBlogPostStructuredData(post, options = {}) {
  if (!post?.slug) return null;

  const readMinutes = options.readMinutes;
  const canonicalPath = post.seo?.canonicalUrl?.startsWith('http')
    ? new URL(post.seo.canonicalUrl).pathname
    : (post.seo?.canonicalUrl || `/blog/${post.slug}`);
  const pageUrl = absUrl(canonicalPath);
  const title = post.seo?.metaTitle || post.title;
  const description = post.seo?.metaDescription || post.excerpt || '';
  const published = toIsoDate(post.publishedAt);
  const modified = toIsoDate(post.updatedAt || post.publishedAt);
  const { categoryName, categorySlug } = resolveBlogCategory(post);

  const customBlogPosting = parseCustomBlogPosting(post.seo?.schemaJson);

  const blogPosting = customBlogPosting || {
    '@type': 'BlogPosting',
    '@id': `${pageUrl}#article`,
    headline: post.title,
    name: title,
    description,
    url: pageUrl,
    datePublished: published,
    dateModified: modified,
    author: post.author
      ? { '@type': 'Person', name: post.author }
      : { '@type': 'Organization', name: 'Gab AI Team' },
    publisher: {
      '@type': 'Organization',
      name: ORG_NAME,
      url: SITE_URL,
      logo: { '@type': 'ImageObject', url: LOGO_URL },
    },
    mainEntityOfPage: { '@id': `${pageUrl}#webpage` },
    speakable: {
      '@type': 'SpeakableSpecification',
      cssSelector: BLOG_SPEAKABLE_SELECTORS,
    },
    inLanguage: 'en-US',
    isAccessibleForFree: true,
    ...(post.coverImage ? { image: post.coverImage } : {}),
    ...(categoryName ? { articleSection: categoryName } : {}),
    ...(post.tags?.length ? { keywords: post.tags.join(', ') } : {}),
    ...(readMinutes > 0 ? { timeRequired: `PT${readMinutes}M` } : {}),
  };

  const webPage = {
    '@type': 'WebPage',
    '@id': `${pageUrl}#webpage`,
    url: pageUrl,
    name: title,
    headline: post.title,
    description,
    inLanguage: 'en-US',
    isPartOf: { '@type': 'WebSite', name: 'Gab AI', url: SITE_URL },
    breadcrumb: { '@id': `${pageUrl}#breadcrumb` },
    datePublished: published,
    dateModified: modified,
    speakable: {
      '@type': 'SpeakableSpecification',
      cssSelector: BLOG_SPEAKABLE_SELECTORS,
    },
    ...(post.coverImage ? { primaryImageOfPage: post.coverImage } : {}),
  };

  const breadcrumb = buildBlogPostBreadcrumbList({
    pageUrl,
    title: post.title,
    categoryName,
    categorySlug,
  });

  return {
    '@context': 'https://schema.org',
    '@graph': [webPage, blogPosting, breadcrumb],
  };
}

export function buildBlogIndexStructuredData() {
  const pageUrl = `${SITE_URL}/blog`;
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Blog',
        '@id': `${pageUrl}#blog`,
        name: 'Gab AI Blog',
        description: 'Latest news, tutorials, and insights from Gab AI.',
        url: pageUrl,
        publisher: {
          '@type': 'Organization',
          name: ORG_NAME,
          url: SITE_URL,
          logo: { '@type': 'ImageObject', url: LOGO_URL },
        },
        inLanguage: 'en-US',
      },
      {
        '@type': 'WebPage',
        '@id': `${pageUrl}#webpage`,
        url: pageUrl,
        name: 'Gab AI Blog',
        isPartOf: { '@type': 'WebSite', name: 'Gab AI', url: SITE_URL },
        breadcrumb: { '@id': `${pageUrl}#breadcrumb` },
        inLanguage: 'en-US',
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${pageUrl}#breadcrumb`,
        itemListElement: [
          listItem(1, 'Home', SITE_URL),
          listItem(2, 'Blog', pageUrl),
        ],
      },
    ],
  };
}

export function buildBlogCategoryStructuredData(cat, posts, slug) {
  const pageUrl = `${SITE_URL}/blog/category/${slug}`;
  const itemList = (posts || []).slice(0, 24).map((post, idx) => ({
    '@type': 'ListItem',
    position: idx + 1,
    url: `${SITE_URL}/blog/${post.slug}`,
    name: post.title,
    ...(post.coverImage ? { image: post.coverImage } : {}),
  }));

  const graph = [
    {
      '@type': 'CollectionPage',
      '@id': `${pageUrl}#collection`,
      name: cat?.meta?.title || (cat?.name ? `${cat.name} — Gab AI Blog` : 'Gab AI Blog'),
      description: cat?.meta?.description || cat?.description || '',
      url: pageUrl,
      isPartOf: { '@type': 'WebSite', name: 'Gab AI', url: SITE_URL },
      breadcrumb: { '@id': `${pageUrl}#breadcrumb` },
      inLanguage: 'en-US',
      ...(itemList.length
        ? {
            mainEntity: {
              '@type': 'ItemList',
              numberOfItems: cat?.stats?.postCount ?? itemList.length,
              itemListElement: itemList,
            },
          }
        : {}),
    },
    {
      '@type': 'BreadcrumbList',
      '@id': `${pageUrl}#breadcrumb`,
      itemListElement: [
        listItem(1, 'Home', SITE_URL),
        listItem(2, 'Blog', `${SITE_URL}/blog`),
        listItem(3, cat?.name || slug, pageUrl),
      ],
    },
  ];

  return { '@context': 'https://schema.org', '@graph': graph };
}

export function blogPublicationDates(post) {
  const published = toIsoDate(post?.publishedAt);
  const modified = toIsoDate(post?.updatedAt || post?.publishedAt);
  const showUpdated = Boolean(published && modified && published !== modified);
  return { published, modified, showUpdated };
}
